import React from 'react';
import SVG from 'react-inlinesvg';
import { useTheme } from 'styled-components';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import { IconColor, IconSize, IconWrapper, SVGWrapper } from './styles';

type Props = {
  name: string;
  size: IconSize;
  color?: IconColor;
  flipX?: boolean;
  flipY?: boolean;
  displayVariant?: boolean;
  'aria-label'?: string;
} & ComponentProps;

const cleanSvg = (color: string) => (code: string): string =>
  code
    // Replace specific fill and stroke colors with current colour
    .replace(/fill="(?!(none))#?\w*"/g, `fill="${color}"`)
    .replace(/stroke="(?!(none))#?\w*"/g, `stroke="${color}"`)
    // Remove ids
    .replace(/id=".*?"/g, '')
    // Remove the title tag
    .replace(/<title>.*?<\/title>/g, '');

/**
 * A component to display an icon from the theme, given the icon's icon code.
 *
 * @param name  - the icon code of the icon to be displayed
 * @param size - the size of the icon - this should be "small", "medium" or "large"
 * @param color - the fill color of the icon
 * @param flipX - icon is flipped about the y-axis when set to true
 * @param flipY - icon is flipped about the x-axis when set to true
 * @param displayVariant - if true, use the "variant" version of the icon (defined on the theme) if it exists. Changing the value of this will cause the icon to transition between the default and variant states using a fade animation.
 */
const Icon: React.FC<Props> = ({
  color,
  name,
  size,
  flipX,
  flipY,
  displayVariant,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const theme = useTheme();
  const variantExists = !!theme.icons[name]?.variant;
  const fillColor =
    typeof color === 'function' ? color({ theme }) : color ?? 'currentColor';

  return (
    <IconWrapper
      fillColor={color}
      flipX={flipX}
      flipY={flipY}
      size={size}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      {...componentProps(rest)}>
      <SVGWrapper aria-hidden active={!displayVariant || !variantExists}>
        <SVG src={theme.icons[name].default} preProcessor={cleanSvg(fillColor)} />
      </SVGWrapper>
      {variantExists && (
        <SVGWrapper aria-hidden active={displayVariant}>
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          <SVG src={theme.icons[name].variant!} preProcessor={cleanSvg(fillColor)} />
        </SVGWrapper>
      )}
    </IconWrapper>
  );
};

export default applyComponentTheme(Icon, 'icon');
